import { initializeApp, getApps, type FirebaseApp } from "firebase/app";
import {
  getAuth,
  GoogleAuthProvider,
  OAuthProvider,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  type Auth,
  type User,
  type UserCredential,
} from "firebase/auth";
import { getDatabase, ref, set, update, type Database } from "firebase/database";
import type { SeedHabit } from "./habitSeeds";

/**
 * Firebase configuration. All fields are public — the Web SDK is designed
 * for client-side use, and security is enforced via Realtime Database
 * Rules + Firebase Security Rules, not via secret config values.
 *
 * Env vars are populated at build time by Vite. Missing values throw a
 * clear error on first use rather than failing silently at runtime.
 */
const config = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  // Optional — drives Google Analytics for Firebase. Web2app doesn't
  // currently load `firebase/analytics`, so this is passthrough only;
  // we keep it in the config so the project's identity matches the
  // canonical web2app Firebase project exactly.
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

let cached: { app: FirebaseApp; auth: Auth; db: Database } | null = null;

export function getFirebase() {
  if (cached) return cached;
  if (!config.apiKey || !config.projectId) {
    throw new Error(
      "Firebase env vars missing. Set VITE_FIREBASE_API_KEY + " +
        "VITE_FIREBASE_PROJECT_ID (+ databaseURL, authDomain, appId) in .env.local."
    );
  }
  const app = getApps()[0] ?? initializeApp(config);
  cached = { app, auth: getAuth(app), db: getDatabase(app) };
  return cached;
}

/* ── Auth helpers ─────────────────────────────────────────────────────── */

export async function signUpWithEmail(
  email: string,
  password: string
): Promise<UserCredential> {
  const { auth } = getFirebase();
  return createUserWithEmailAndPassword(auth, email, password);
}

export async function signInWithEmail(
  email: string,
  password: string
): Promise<UserCredential> {
  const { auth } = getFirebase();
  return signInWithEmailAndPassword(auth, email, password);
}

export async function signInWithGoogle(): Promise<UserCredential> {
  const { auth } = getFirebase();
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  return signInWithPopup(auth, provider);
}

export async function signInWithApple(): Promise<UserCredential> {
  const { auth } = getFirebase();
  const provider = new OAuthProvider("apple.com");
  provider.addScope("email");
  provider.addScope("name");
  return signInWithPopup(auth, provider);
}

/* ── Realtime Database writes ────────────────────────────────────────── */

/**
 * Write a batch of seed habits to `/habits/{uid}/{habitId}`.
 *
 * Path + schema are verified against the live habitify-web codebase:
 *   src/renderer/src/services/habits-realtime-db-services/index.ts:389
 *   src/renderer/src/realtime-schemas/habit-item.ts
 *
 * Notably the path is `habits/{uid}/{id}` — NOT `users/{uid}/habits/{id}`.
 * Each habit is a flat node keyed by UUID. Habitify clients pick these up
 * on next sync because they're already subscribed via onValue().
 */
export async function writeSeedHabits(
  user: User,
  habits: SeedHabit[]
): Promise<void> {
  const { db } = getFirebase();
  await Promise.all(
    habits.map((habit) => {
      const { habitId, __source, ...rest } = habit;
      // We tag the source for our own analytics replay but strip it from
      // the canonical write to keep the row matching RealtimeHabitItem.
      void __source;
      return set(ref(db, `habits/${user.uid}/${habitId}`), rest);
    })
  );
}

/**
 * Optionally upsert the user's profile fields under `/users/{uid}` so the
 * Habitify app picks up the display name + avatar when it first reads
 * them on launch. Path verified at users.model.ts:62. Missing fields are
 * simply not written.
 */
export async function writeUserProfile(user: User): Promise<void> {
  const { db } = getFirebase();
  const patch: Record<string, unknown> = {};
  if (user.displayName) {
    patch.name = user.displayName;
    const [first, ...rest] = user.displayName.split(" ");
    if (first) patch.firstName = first;
    if (rest.length) patch.lastName = rest.join(" ");
  }
  if (user.photoURL) patch.profileImage = user.photoURL;
  if (Object.keys(patch).length === 0) return;
  await update(ref(db, `users/${user.uid}`), patch);
}

/** Used by analytics — pulls a readable provider id off the credential. */
export function providerOf(cred: UserCredential): string {
  return cred.providerId ?? cred.user.providerData[0]?.providerId ?? "unknown";
}

/**
 * Synchronous read of the currently-signed-in Firebase user, if any.
 * Safe to call before onAuthStateChanged has fired (returns null).
 * Used by the paywall to decide whether to show the pre-checkout
 * signup modal — if the user already signed in earlier in the session,
 * we skip straight to Paddle.
 */
export function getCurrentUser(): User | null {
  try {
    return getFirebase().auth.currentUser;
  } catch {
    // getFirebase throws when env vars aren't configured (local dev
    // without Firebase set up). Treat as signed-out.
    return null;
  }
}

/**
 * Subscribe to Firebase auth state changes. Fires once with the
 * current user on mount, then again on every signin/signout. Returns
 * an unsubscribe function — call from useEffect cleanup.
 */
export function onAuthChange(cb: (user: User | null) => void): () => void {
  try {
    return onAuthStateChanged(getFirebase().auth, cb);
  } catch {
    // Same no-Firebase case — pretend the user is signed out and
    // never fires again.
    cb(null);
    return () => {};
  }
}
